/**
 * The LiveKit room of one Firmio Meet meeting, scoped to the meeting page.
 *
 * Deliberately NOT the app-level VoiceRoomProvider: that one follows you
 * around the product so a voice channel survives navigation, while a meeting
 * is a full-screen page you are either on or not — leaving the page IS
 * leaving the meeting, and guests have no app shell to carry a call in. The
 * meeting page mounts outside the Layout, so the voice provider unmounts and
 * hangs up any voice channel on its own.
 *
 * Camera tracks are read straight off the room on every sync rather than
 * bookkept per event: every LiveKit event that could change who publishes
 * what just triggers a rebuild, which is cheap and cannot drift.
 */
import {
  ConnectionState,
  DisconnectReason,
  Room,
  RoomEvent,
  Track,
  type Participant,
  type RemoteTrack,
  type RemoteTrackPublication,
} from 'livekit-client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { VoiceScreenShare } from '../contexts/VoiceRoomContext';
import type { MeetGrant } from '../lib/meetApi';

export interface MeetMember {
  /** The participant row id — LiveKit identity for meetings. */
  id: string;
  name: string;
  isLocal: boolean;
  micOn: boolean;
  camOn: boolean;
  sharingScreen: boolean;
  cameraTrack: Track | null;
}

export type MeetRoomStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'ended';

export type MeetRoomErrorKey =
  | 'meet.error.micDenied'
  | 'meet.error.cameraDenied'
  | 'meet.error.connect';

function toMember(p: Participant, isLocal: boolean): MeetMember {
  const cam = p.getTrackPublication(Track.Source.Camera);
  return {
    id: p.identity,
    name: p.name || p.identity,
    isLocal,
    micOn: p.isMicrophoneEnabled,
    camOn: p.isCameraEnabled && Boolean(cam?.track) && !cam?.isMuted,
    sharingScreen: p.isScreenShareEnabled,
    cameraTrack: cam?.track ?? null,
  };
}

function toShare(p: Participant, isLocal: boolean): VoiceScreenShare | null {
  const pub = p.getTrackPublication(Track.Source.ScreenShare);
  if (!pub?.track) return null;
  return {
    track: pub.track,
    publisherId: p.identity,
    publisherName: p.name || p.identity,
    isLocal,
  };
}

/** Data-channel topic for "I am recording" notices. */
const RECORDING_TOPIC = 'recording';

export function useMeetRoom() {
  const roomRef = useRef<Room | null>(null);
  const audioRef = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<MeetRoomStatus>('idle');
  const [members, setMembers] = useState<MeetMember[]>([]);
  const [speakingIds, setSpeakingIds] = useState<string[]>([]);
  const [screenShares, setScreenShares] = useState<VoiceScreenShare[]>([]);
  const [micEnabled, setMicEnabled] = useState(false);
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [screenShareEnabled, setScreenShareEnabled] = useState(false);
  const [endedByHost, setEndedByHost] = useState(false);
  // The host put us out (RoomService.removeParticipant): shown as its own
  // message, not as "you left".
  const [removedByHost, setRemovedByHost] = useState(false);
  const [error, setError] = useState<MeetRoomErrorKey | null>(null);
  // Name of the participant who announced they are recording (data channel
  // topic "recording"), so everyone else sees the red banner. Null = nobody.
  const [recordingBy, setRecordingBy] = useState<string | null>(null);

  const sync = useCallback(() => {
    const room = roomRef.current;
    if (!room) {
      setMembers([]);
      setScreenShares([]);
      return;
    }
    const remote = [...room.remoteParticipants.values()];
    setMembers([toMember(room.localParticipant, true), ...remote.map((p) => toMember(p, false))]);
    setScreenShares(
      [toShare(room.localParticipant, true), ...remote.map((p) => toShare(p, false))].filter(
        (s): s is VoiceScreenShare => s !== null,
      ),
    );
    setMicEnabled(room.localParticipant.isMicrophoneEnabled);
    setCameraEnabled(room.localParticipant.isCameraEnabled);
    setScreenShareEnabled(room.localParticipant.isScreenShareEnabled);
  }, []);

  const teardown = useCallback(() => {
    roomRef.current = null;
    setMembers([]);
    setSpeakingIds([]);
    setScreenShares([]);
    setMicEnabled(false);
    setCameraEnabled(false);
    setScreenShareEnabled(false);
  }, []);

  const leave = useCallback(async () => {
    const room = roomRef.current;
    teardown();
    setStatus('idle');
    if (room) await room.disconnect();
  }, [teardown]);

  const connect = useCallback(
    async (grant: MeetGrant, options: { mic: boolean; camera: boolean }) => {
      if (roomRef.current) await leave();
      setError(null);
      setEndedByHost(false);
      setRemovedByHost(false);
      setStatus('connecting');

      // adaptiveStream is fine here, unlike the voice channels: every tile is
      // on screen, so nothing gets paused for being invisible.
      const room = new Room({ adaptiveStream: true, dynacast: true });
      roomRef.current = room;

      room
        .on(RoomEvent.ParticipantConnected, sync)
        .on(RoomEvent.ParticipantDisconnected, sync)
        .on(RoomEvent.TrackPublished, sync)
        .on(RoomEvent.TrackUnpublished, sync)
        .on(RoomEvent.TrackMuted, sync)
        .on(RoomEvent.TrackUnmuted, sync)
        .on(RoomEvent.LocalTrackPublished, sync)
        .on(RoomEvent.LocalTrackUnpublished, sync)
        .on(RoomEvent.ActiveSpeakersChanged, (speakers: Participant[]) =>
          setSpeakingIds(speakers.map((s) => s.identity)),
        )
        .on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
          if (track.kind === Track.Kind.Audio) audioRef.current?.appendChild(track.attach());
          sync();
        })
        .on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack, _pub: RemoteTrackPublication) => {
          if (track.kind === Track.Kind.Audio) track.detach().forEach((el) => el.remove());
          sync();
        })
        .on(RoomEvent.ConnectionStateChanged, (state: ConnectionState) => {
          if (state === ConnectionState.Connected) setStatus('connected');
          else if (state === ConnectionState.Reconnecting) setStatus('reconnecting');
        })
        .on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
          if (roomRef.current !== room) return;
          // The host closed the meeting for everyone: LiveKit deleted the room.
          if (reason === DisconnectReason.ROOM_DELETED) setEndedByHost(true);
          if (reason === DisconnectReason.PARTICIPANT_REMOVED) setRemovedByHost(true);
          teardown();
          setStatus('ended');
        })
        .on(RoomEvent.MediaDevicesError, (err: Error) => {
          setError(err.name === 'NotAllowedError' ? 'meet.error.micDenied' : 'meet.error.connect');
        })
        .on(
          RoomEvent.DataReceived,
          (payload: Uint8Array, participant?: Participant, _kind?: unknown, topic?: string) => {
            if (topic !== RECORDING_TOPIC) return;
            try {
              const msg = JSON.parse(new TextDecoder().decode(payload)) as { on?: boolean };
              const name = participant?.name || participant?.identity || '';
              setRecordingBy(msg.on ? name || '?' : null);
            } catch {
              // not ours
            }
          },
        )
        .on(RoomEvent.ParticipantDisconnected, (participant: Participant) => {
          // The recorder left: the banner goes with them.
          setRecordingBy((who) =>
            who && (who === participant.name || who === participant.identity) ? null : who,
          );
        });

      try {
        await room.connect(grant.url, grant.token);
      } catch {
        setError('meet.error.connect');
        await room.disconnect().catch(() => undefined);
        if (roomRef.current === room) teardown();
        setStatus('idle');
        return;
      }
      // Devices after the connection: a refused microphone or camera leaves
      // you in the room listening, not bounced out.
      if (options.mic) {
        try {
          await room.localParticipant.setMicrophoneEnabled(true);
        } catch {
          setError('meet.error.micDenied');
        }
      }
      if (options.camera) {
        try {
          await room.localParticipant.setCameraEnabled(true);
        } catch {
          setError('meet.error.cameraDenied');
        }
      }
      setStatus('connected');
      sync();
    },
    [leave, sync, teardown],
  );

  const toggleMic = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const next = !room.localParticipant.isMicrophoneEnabled;
    setMicEnabled(next);
    try {
      await room.localParticipant.setMicrophoneEnabled(next);
    } catch {
      setError('meet.error.micDenied');
    }
    sync();
  }, [sync]);

  const toggleCamera = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const next = !room.localParticipant.isCameraEnabled;
    setCameraEnabled(next);
    try {
      await room.localParticipant.setCameraEnabled(next);
    } catch {
      setError('meet.error.cameraDenied');
    }
    sync();
  }, [sync]);

  const toggleScreenShare = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const next = !room.localParticipant.isScreenShareEnabled;
    try {
      await room.localParticipant.setScreenShareEnabled(next, { audio: true });
    } catch (err) {
      // Dismissing the OS picker is a decision, not a failure.
      if (!(err instanceof Error && err.name === 'NotAllowedError')) setError('meet.error.connect');
    }
    sync();
  }, [sync]);

  /** Tell everyone in the room whether we are recording (they show a banner). */
  const announceRecording = useCallback((on: boolean) => {
    const room = roomRef.current;
    if (!room) return;
    void room.localParticipant
      .publishData(new TextEncoder().encode(JSON.stringify({ on })), {
        reliable: true,
        topic: RECORDING_TOPIC,
      })
      .catch(() => undefined);
  }, []);

  const dismissError = useCallback(() => setError(null), []);

  useEffect(() => {
    return () => {
      void roomRef.current?.disconnect();
      roomRef.current = null;
    };
  }, []);

  return useMemo(
    () => ({
      /** The live SDK room, for the transcriber. Null when not connected. */
      getRoom: () => roomRef.current,
      audioRef,
      status,
      members,
      speakingIds,
      screenShares,
      micEnabled,
      cameraEnabled,
      screenShareEnabled,
      endedByHost,
      removedByHost,
      error,
      recordingBy,
      connect,
      leave,
      toggleMic,
      toggleCamera,
      toggleScreenShare,
      announceRecording,
      dismissError,
    }),
    [
      status,
      members,
      speakingIds,
      screenShares,
      micEnabled,
      cameraEnabled,
      screenShareEnabled,
      endedByHost,
      removedByHost,
      error,
      recordingBy,
      connect,
      leave,
      toggleMic,
      toggleCamera,
      toggleScreenShare,
      announceRecording,
      dismissError,
    ],
  );
}

export type MeetRoomHandle = ReturnType<typeof useMeetRoom>;
